import React, { useEffect, useMemo, useState } from 'react';
import { WorldSection } from './WorldPageHeader';
import { CopyTextButton } from './CopyTextButton';
import { auditInvasions, invasionChecks } from '../../utils/invasionValidation';
import { effectiveRouteQuality, rewardVariantProfileKeys, rewardVariantQuality } from '../../utils/rewardQuality';
import { fetchCsv } from '../../utils/csv';
import type { Row } from '../../utils/csv';
import { packageUrl } from '../../data/world/package';
import type { WorldData } from '../../types/world';
const WW_ROUTE_ID = 'route:027eeb58afc8bf0ed4';
const BAG_SECTIONS_URL = packageUrl('19_bag_sections.csv');
const RAW_BAG_ROWS_URL = packageUrl('26_raw_bag_rows.csv');
const verdictText = {
  authorized: 'Autorizado',
  unlisted: 'En revisión: sin lista vigente o pendiente de aprobación',
  conflict: 'Conflicto entre fuentes'
} as const;
const verdictTone = {
  authorized: 'text-emerald-200',
  unlisted: 'text-amber-200',
  conflict: 'text-red-200'
} as const;
type BagState = {
  status: 'loading' | 'ok' | 'error';
  sections: Row[];
  raw: Row[];
  error?: string;
};

/** Panel técnico: contraste entre rutas de invasión exportadas y la información aprobada. */
export function InvasionDropCheck({
  w
}: {
  w: WorldData;
}) {
  const audit = useMemo(() => auditInvasions(w), [w]);
  const [bag, setBag] = useState<BagState>({
    status: 'loading',
    sections: [],
    raw: []
  });
  useEffect(() => {
    let alive = true;
    Promise.all([fetchCsv(BAG_SECTIONS_URL), fetchCsv(RAW_BAG_ROWS_URL)]).then(([s, r]) => alive && setBag({
      status: 'ok',
      sections: s.rows,
      raw: r.rows,
      error: [...s.errors, ...r.errors].slice(0, 5).join(' · ') || undefined
    })).catch((e: Error) => alive && setBag({
      status: 'error',
      sections: [],
      raw: [],
      error: e.message
    }));
    return () => {
      alive = false;
    };
  }, []);
  const dragonHelm = useMemo(() => {
    const routes = w.routes.filter(r => r.itemId === '7,1');
    return routes.map(r => ({
      r,
      checks: invasionChecks(w, r)
    }));
  }, [w]);

  // Casos pedidos: Dorados (equipo dentro de cajas Kundun) y la ruta del White Wizard (calidad por perfil).
  const specificCases = useMemo(() => {
    const golden = ['7,1', '0,0'].flatMap(itemId => w.routes.filter(r => r.itemId === itemId).flatMap(r => invasionChecks(w, r).filter(c => c.policy.guideEntryId === 'golden').map(c => ({
      r,
      c,
      eq: effectiveRouteQuality(w, r)
    }))));
    const wwRoute = w.idx.routeById.get(WW_ROUTE_ID) ?? w.idx.routeById.get(WW_ROUTE_ID.replace(/^route:/, ''));
    const ww = wwRoute ? {
      r: wwRoute,
      checks: invasionChecks(w, wwRoute),
      eq: effectiveRouteQuality(w, wwRoute),
      profileKeys: rewardVariantProfileKeys(w, '377'),
      v377: rewardVariantQuality(w, '377')
    } : undefined;
    return {
      golden,
      ww
    };
  }, [w]);
  const bag2Sections = bag.sections.filter(s => s.bag_id === '2');
  const bag2Raw = bag.raw.filter(s => s.bag_id === '2');
  const text = useMemo(() => {
    const lines = ['CONTRASTE DE INVASIONES — rutas exportadas frente a información aprobada', ''];
    audit.forEach(a => {
      lines.push(`## ${a.policy.name} (${a.policy.eventIds.join(', ')}${a.policy.bagIds.length ? ` · bag ${a.policy.bagIds.join(', ')}` : ''}${a.policy.monsterIds.length ? ` · monster ${a.policy.monsterIds.join(', ')}` : ''})`);
      lines.push(`Aprobado: ${a.policy.approved}`);
      lines.push(`Rutas: ${a.routes} · ítems distintos: ${a.items} · autorizados ${a.authorized}${a.indirect ? ` (${a.indirect} como recompensa indirecta: requieren abrir la caja)` : ''} · sin lista vigente ${a.unlisted} · conflicto ${a.conflict}`);
      if (a.policy.missing) lines.push(`Falta: ${a.policy.missing}`);
      a.rows.filter(x => x.verdict !== 'authorized').forEach(x => lines.push(`- [${verdictText[x.verdict]}] ${x.name} (${x.itemId}) ${x.quality ?? ''} · ${x.routeId} · ${x.reason}`));
      lines.push('');
    });
    lines.push('## Dorados: equipo obtenido al abrir cajas Kundun (monstruo → caja → resultado)');
    if (!specificCases.golden.length) lines.push('- Sin rutas de Dragon Helm (7,1) ni Kris (0,0) asociadas a invasion:2 en los archivos cargados.');
    specificCases.golden.forEach(({
      r,
      c
    }) => lines.push(`- ${w.items.get(r.itemId ?? '')?.name ?? r.itemId} · ${r.id} → ${verdictText[c.verdict]}${c.indirectVia ? ` · vía ${c.indirectVia.boxName}` : ''} · ${c.reason}`));
    lines.push('', `## White Wizard · ruta ${WW_ROUTE_ID}`);
    if (!specificCases.ww) lines.push('- La ruta no está en los archivos cargados (o la retiró el filtro de publicación).');else {
      const {
        r,
        checks,
        eq,
        profileKeys,
        v377
      } = specificCases.ww;
      lines.push(`- Ítem ${r.itemId} · bag ${r.bagId ?? '—'} · calidad en la ruta: ${r.quality || 'vacía'}`);
      lines.push(`- variant:377 → perfiles ${profileKeys.join(', ') || 'ninguno encontrado en 22/23'} · excellent_count ${v377 ? `${v377.excellentMin}${v377.excellentMax !== v377.excellentMin ? `–${v377.excellentMax}` : ''}` : 'no resuelto en 25_option_profiles'}`);
      lines.push(`- Calidad efectiva: ${eq.quality} (${eq.source}) · ${eq.note}`);
      checks.forEach(c => {
        lines.push(`- ${c.policy.name}: ${verdictText[c.verdict]} · ${c.reason}`);
        lines.push(`  · Configuración: ${c.states.config}`, `  · Vigencia: ${c.states.validity}`, `  · Juego: ${c.states.inGame}`);
      });
      if (eq.conditional) lines.push('- El 100 % del perfil está condicionado a que salga este resultado: no es una probabilidad del 100 % por muerte.');
    }
    lines.push('');
    lines.push('## Dragon Helm (7,1)');
    dragonHelm.forEach(({
      r,
      checks
    }) => lines.push(`- ${r.id} · ${r.kind} · ${r.quality ?? '—'} · bag ${r.bagId ?? '—'} → ${checks.length ? checks.map(c => `${c.policy.name}: ${verdictText[c.verdict]}`).join('; ') : 'no es ruta de invasión (se mantiene)'}`));
    lines.push('', `## bag 2 en 21_bag_rewards.csv: ${w.bagRewards.filter(r => r.bagId === '2').length} filas · ${new Set(w.bagRewards.filter(r => r.bagId === '2').map(r => r.itemId)).size} ítems · 7,1 incluido: ${w.bagRewards.some(r => r.bagId === '2' && r.itemId === '7,1') ? 'sí' : 'no'}`);
    lines.push(`## bag 2 en 19_bag_sections.csv:`);
    lines.push(`   detalle: ${bag.status === 'ok' ? `${bag2Sections.length} secciones · used_by_runtime: ${bag2Sections.map(s => `${s.section_id}=${s.used_by_runtime || '—'}`).join(', ')}` : bag.status}`);
    lines.push(`## bag 2 en raw_bag_rows.csv: ${bag.status === 'ok' ? `${bag2Raw.length} líneas` : bag.status}`);
    return lines.join('\n');
  }, [w, audit, dragonHelm, specificCases, bag, bag2Sections, bag2Raw]);
  return <WorldSection id="invasiones-drops" title="Drops de invasión: exportación frente a lo aprobado" data-id="element-1388" data-mp="24rqeazc4gtpf">
      <p className="mb-4 max-w-3xl text-sm text-gray-400" data-id="element-1389" data-mp="1v03y0okxg4ft">
        Una ruta de invasión solo se muestra a los jugadores si el ítem está autorizado de forma explícita. Los casos “Sin lista vigente” y “Conflicto entre fuentes” se retienen; las demás formas de obtener esos ítems se mantienen. Los archivos originales no se modifican.
      </p>
      <div className="mb-5" data-id="element-1390" data-mp="12enmr6zachog">
        <CopyTextButton text={text} label="Copiar contraste de invasiones" data-id="element-1391" data-mp="12dw3mvmvkcf2" />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1392" data-mp="xg41z9wfhbxo">
        <table className="w-full min-w-[640px] text-left text-sm" data-id="element-1393" data-mp="1bfdg8412pvz0">
          <thead className="bg-white/[0.04] text-gray-400" data-id="element-1394" data-mp="16fhde8444al1">
            <tr data-id="element-1395" data-mp="16dwj8jlpmtnp">
              <th className="px-4 py-3 font-semibold" data-id="element-1396" data-mp="16hkd5jv2hi2y">Invasión</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1397" data-mp="1laxayn3wiom6">Rutas</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1398" data-mp="1gdx5oeevlpw4">Ítems</th>
              <th className="px-4 py-3 font-semibold text-emerald-200" data-id="element-1399" data-mp="1gbkpa5ornnl6">Autorizados</th>
              <th className="px-4 py-3 font-semibold text-amber-200" data-id="element-1400" data-mp="1u40oevejr1gt">Sin lista</th>
              <th className="px-4 py-3 font-semibold text-red-200" data-id="element-1401" data-mp="1q3qh9ff0q50d">Conflicto</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-id="element-1402" data-mp="2439ehd6ajvxw">
            {audit.map(a => <tr key={a.policy.guideEntryId} data-id="element-1403" data-mp="1z5g3m6u1mdra">
                <td className="px-4 py-3 align-top" data-id="element-1404" data-mp="36gm26irhcrjc">
                  <span className="font-semibold text-white" data-id="element-1405" data-mp="36g2ml8payf8h">{a.policy.name}</span>
                  {a.policy.missing && <span className="mt-1 block text-xs text-gray-500" data-id="element-1406" data-mp="3ag4fotgz20c5">Falta: {a.policy.missing}</span>}
                </td>
                <td className="px-4 py-3 font-mono text-white" data-id="element-1407" data-mp="3kg3makttzgmd">{a.routes}</td>
                <td className="px-4 py-3 font-mono text-white" data-id="element-1408" data-mp="3kfbz9yeaur7p">{a.items}</td>
                <td className="px-4 py-3 font-mono text-emerald-200" data-id="element-1409" data-mp="2mto23ba0s4v4">{a.authorized}</td>
                <td className="px-4 py-3 font-mono text-amber-200" data-id="element-1410" data-mp="2rshj3doime01">{a.unlisted}</td>
                <td className="px-4 py-3 font-mono text-red-200" data-id="element-1411" data-mp="2wq9a85hc5w36">{a.conflict}</td>
              </tr>)}
          </tbody>
        </table>
      </div>

      <h3 className="mt-6 mb-2 font-semibold text-white" data-id="element-1412" data-mp="31j2fcugpv6fs">Dorados: equipo dentro de cajas Kundun</h3>
      <ul className="space-y-1.5 text-sm" data-id="element-1413" data-mp="24011134s2m0q">
        {specificCases.golden.length === 0 && <li className="text-gray-400" data-id="element-1414" data-mp="28z3x3y2s5hwk">Sin rutas de Dragon Helm ni Kris asociadas a los Dorados en los archivos cargados.</li>}
        {specificCases.golden.map(({
        r,
        c
      }) => <li key={r.id} className="rounded-xl bg-white/[0.03] px-3 py-2 text-gray-300" data-id="element-1415" data-mp="1wjnqd0fy1g16">
            <span className="text-white" data-id="element-1416" data-mp="1ri85p9hohbjb">{w.items.get(r.itemId ?? '')?.name ?? r.itemId}</span> · <span className="font-mono" data-id="element-1417" data-mp="1mky6510krjhr">{r.id}</span> →{' '}
            <span className={verdictTone[c.verdict]} data-id="element-1418" data-mp="1mmj0oqmw32fm">{verdictText[c.verdict]}</span>
            {c.indirectVia && <span data-id="element-1419" data-mp="1ikebc43h4rys"> · requiere abrir {c.indirectVia.boxName}</span>}
            <span className="mt-0.5 block text-xs text-gray-500" data-id="element-1420" data-mp="1dqa2ffane9v2">{c.reason}</span>
          </li>)}
      </ul>

      <h3 className="mt-6 mb-2 font-semibold text-white" data-id="element-1421" data-mp="18qnof3mzwpl7">White Wizard: calidad desde el perfil</h3>
      {specificCases.ww ? <div className="rounded-xl bg-white/[0.03] px-3 py-2 text-sm text-gray-300" data-id="element-1422" data-mp="yuca683uhpo4">
          <p data-id="element-1423" data-mp="ywexgkdm54p5">
            <span className="font-mono text-white" data-id="element-1424" data-mp="ut8v3ntmfxny">{WW_ROUTE_ID}</span> · bag {specificCases.ww.r.bagId ?? '—'} · calidad en la ruta: {specificCases.ww.r.quality || 'vacía'}
          </p>
          <p className="mt-1" data-id="element-1425" data-mp="1drqhmctfoj1o">
            variant:377 → perfiles {specificCases.ww.profileKeys.join(', ') || 'no encontrados'} · excellent_count{' '}
            {specificCases.ww.v377 ? `${specificCases.ww.v377.excellentMin}${specificCases.ww.v377.excellentMax !== specificCases.ww.v377.excellentMin ? `–${specificCases.ww.v377.excellentMax}` : ''}` : 'no resuelto'}
          </p>
          <p className="mt-1" data-id="element-1426" data-mp="zu1xx8b3rhy7">Calidad efectiva: {specificCases.ww.eq.quality} ({specificCases.ww.eq.source})</p>
          {specificCases.ww.checks.map(c => <p key={c.policy.guideEntryId} className={`mt-1 ${verdictTone[c.verdict]}`} data-id="element-1427" data-mp="18towlmcg69nk">
              {c.policy.name}: {verdictText[c.verdict]} · <span className="text-gray-400" data-id="element-1428" data-mp="14t526qj9dk54">{c.reason}</span>
              <span className="mt-1 grid gap-0.5 text-xs text-gray-400 sm:grid-cols-3" data-id="element-1429" data-mp="2k6ri8vtelpel">
                <span data-id="element-1430" data-mp="2g6z3209i8l09">Configuración: {c.states.config}</span>
                <span data-id="element-1431" data-mp="2g8k1w22uqq3x">Vigencia: {c.states.validity}</span>
                <span data-id="element-1432" data-mp="2noxc63nrxu2y">Juego: {c.states.inGame}</span>
              </span>
            </p>)}
        </div> : <p className="text-sm text-gray-400" data-id="element-1433" data-mp="2smqdoje4tzww">La ruta {WW_ROUTE_ID} no está en los archivos cargados.</p>}

      <h3 className="mt-6 mb-2 font-semibold text-white" data-id="element-1434" data-mp="2dtx053if7tuv">Caso de verificación: Dragon Helm (7,1)</h3>
      <ul className="space-y-1.5 text-sm" data-id="element-1435" data-mp="2isgjqx9jktk2">
        {dragonHelm.length === 0 && <li className="text-gray-400" data-id="element-1436" data-mp="36gzurkjz7kfu">Sin rutas para 7,1 en los archivos cargados.</li>}
        {dragonHelm.map(({
        r,
        checks
      }) => <li key={r.id} className="rounded-xl bg-white/[0.03] px-3 py-2 text-gray-300" data-id="element-1437" data-mp="2wqemt6iex3k9">
            <span className="font-mono text-white" data-id="element-1438" data-mp="2woka2fhaohr9">{r.id}</span> · {r.kind} · {r.quality ?? '—'}{r.bagId ? ` · bag ${r.bagId}` : ''} →{' '}
            {checks.length ? checks.map(c => <span key={c.policy.guideEntryId} className={verdictTone[c.verdict]} data-id="element-1439" data-mp="3pdr9dgokpbdk">{c.policy.name}: {verdictText[c.verdict]} </span>) : <span className="text-emerald-200" data-id="element-1440" data-mp="3u7n6yi0xcw87">no es ruta de invasión, se mantiene</span>}
          </li>)}
      </ul>

      <h3 className="mt-6 mb-2 font-semibold text-white" data-id="element-1441" data-mp="2wsbi2f8e14b">Red Dragon · bolsa 2 (paquete 08/10/2026)</h3>
      <ul className="space-y-1 text-sm text-gray-300" data-id="element-1442" data-mp="3v0l1zpwayfpq">
        <li data-id="element-1443" data-mp="3v37bhv2r75al">16_bags: {w.bags.get('2') ? `${w.bags.get('2')!.name ?? 'sin nombre'} · delivery_mode ${w.bags.get('2')!.deliveryMode ?? '—'} · used_by_runtime ${String(w.bags.get('2')!.usedByRuntime ?? '—')}` : 'no figura'}</li>
        <li data-id="element-1444" data-mp="3gc6o8jxcl6ue">17_bag_triggers: {(w.idx.bagTriggersByBag.get('2') ?? []).map(t => t.itemId ? `ítem ${t.itemId}` : t.monsterId !== undefined ? `monstruo ${t.monsterId}` : t.id).join(', ') || 'sin activadores'}</li>
        <li data-id="element-1445" data-mp="37c1v9eswiazr">21_bag_rewards: {w.bagRewards.filter(r => r.bagId === '2').length} filas · {new Set(w.bagRewards.filter(r => r.bagId === '2').map(r => r.itemId)).size} ítems distintos</li>
        <li data-id="element-1446" data-mp="2xdm4xe1p75rb">34_event_bags: {(w.idx.eventBagsByBag.get('2') ?? []).map(e => `${e.eventId}${e.monsterId !== undefined ? ` · monstruo ${e.monsterId}` : ''}`).join(', ') || 'sin evento'}</li>
        <li data-id="element-1447" data-mp="1huedk4769pfn">
          19_bag_sections / 26_raw_bag_rows:{' '}
          {bag.status === 'loading' ? 'cargando…' : bag.status === 'error' ? `error de carga: ${bag.error}` : `${bag2Sections.length} secciones y ${bag2Raw.length} líneas de configuración.`}
        </li>
      </ul>
      <p className="mt-2 text-xs text-gray-500" data-id="element-1448" data-mp="1qsyrks1sfaxj">
        La bolsa 2 es una lista interna de resultados posibles al derrotar al jefe, no una caja que abre el jugador. Sus ítems siguen en revisión para los jugadores hasta que la administración confirme la lista vigente.
      </p>
    </WorldSection>;
}